import {
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
  HttpErrorResponse,
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Observable, throwError, EMPTY, of, TimeoutError } from "rxjs";
import { catchError, switchMap, shareReplay, finalize, tap, timeout } from "rxjs/operators";
import { MsalService } from "@azure/msal-angular";
import { environment } from "src/environments/environment";

/**
 * Authentication Interceptor Service
 * 
 * Responsibilities:
 * 1. Adds Bearer tokens to authorized HTTP requests
 * 2. Handles token acquisition with progressive fallback: silent → popup → redirect
 * 3. Prevents multiple concurrent MSAL interactions (popup/redirect conflicts)
 * 4. Manages token refresh on authentication errors
 * 
 * Token Acquisition Strategy (to minimize redirects):
 * - FIRST: Try acquireTokenSilent (no user interaction)
 * - SECOND: Try acquireTokenPopup (modal, better UX)
 * - LAST RESORT: Use acquireTokenRedirect (only if popup blocked)
 */
@Injectable()
export class AuthInterceptorService implements HttpInterceptor {
  
  // Shared observable for ongoing token refresh to prevent concurrent interactions
  private tokenRefreshInProgress$: Observable<any> | null = null;
  
  // Flag to prevent race conditions when setting tokenRefreshInProgress$
  private isAcquiringToken = false;
  
  // Request counter for debugging
  private requestCounter = 0;
  
  // Retry tracking to prevent infinite loops
  private retryCount = new Map<string, number>();
  private readonly MAX_RETRY_ATTEMPTS = 2;
  
  // MSAL operation timeout (30 seconds)
  private readonly MSAL_TIMEOUT = 30000;

  constructor(
    private msalService: MsalService
  ) { }

  /**
   * Generate unique request ID for retry tracking
   */
  private getRequestId(req: HttpRequest<any>): string {
    return `${req.method}:${req.url}`;
  }

  /**
   * Main interceptor entry point
   * Routes requests based on whether they need authentication
   */
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    this.requestCounter++;
    const requestId = this.requestCounter;
    console.log(`[Auth-Interceptor #${requestId}] ========== NEW REQUEST ==========`);
    console.log(`[Auth-Interceptor #${requestId}] Method: ${req.method}`);
    console.log(`[Auth-Interceptor #${requestId}] URL: ${req.url}`);
    console.log(`[Auth-Interceptor #${requestId}] Headers:`, req.headers);
    console.log(`[Auth-Interceptor #${requestId}] Request body:`, req.body);

    if (this.shouldSkipAuth(req, requestId)) {
      console.log(`[Auth-Interceptor #${requestId}] ✓ Decision: SKIP authentication`);
      console.log(`[Auth-Interceptor #${requestId}]   Reason: URL matches skip criteria`);
      return this.handleNonAuthRequest(req, next, requestId);
    }

    console.log(`[Auth-Interceptor #${requestId}] ✓ Decision: REQUIRES authentication`);
    return this.handleAuthRequest(req, next, requestId);
  }

  // ==================== URL FILTERING ====================

  /**
   * Determines if a request should bypass authentication
   * @returns true if request doesn't need auth token
   */
  private shouldSkipAuth(req: HttpRequest<any>, requestId: number): boolean {
    const url = req.url;
    const method = req.method.toUpperCase();

    const isProd157 = url.includes("prod-157");
    const isMsLogin = url.includes("login.microsoftonline.com");
    const isAssets = url.includes("assets/");
    const isTranslationGet = url.includes("api/v1/masterdata/translation") && method === "GET";

    const shouldSkip = isProd157 || isMsLogin || isAssets || isTranslationGet;

    if (shouldSkip) {
      console.log(`[Auth-Interceptor #${requestId}] [shouldSkipAuth] YES - Matched:`, {
        prod157: isProd157,
        msLogin: isMsLogin,
        assets: isAssets,
        translationGet: isTranslationGet
      });
    } else {
      console.log(`[Auth-Interceptor #${requestId}] [shouldSkipAuth] NO - Auth required`);
    }

    return shouldSkip;
  }

  /**
   * Checks if a URL should never trigger token refresh
   * (special cases like file downloads, attachments)
   */
  private isNonRefreshableUrl(req: HttpRequest<any>, requestId: number): boolean {
    const hasDamageDocs = req.url.includes("damages/detail/docs");
    const hasAttachment = req.url.includes("attachment");
    const result = hasDamageDocs || hasAttachment;

    if (result) {
      console.log(`[Auth-Interceptor #${requestId}] [isNonRefreshableUrl] YES - URL is non-refreshable:`, {
        url: req.url,
        hasDamageDocs,
        hasAttachment
      });
    }

    return result;
  }

  // ==================== REQUEST HANDLERS ====================

  /**
   * Handles requests that don't need auth tokens
   * Still monitors for 401/400 errors that may indicate auth issues
   */
  private handleNonAuthRequest(
    req: HttpRequest<any>,
    next: HttpHandler,
    requestId: number
  ): Observable<HttpEvent<any>> {
    console.log(`[Auth-Interceptor #${requestId}] [handleNonAuthRequest] Executing request WITHOUT token`);
    
    return next.handle(req).pipe(
      tap(() => console.log(`[Auth-Interceptor #${requestId}] [handleNonAuthRequest] ✓ Request succeeded`)),
      catchError((error: HttpErrorResponse) => {
        console.log(`[Auth-Interceptor #${requestId}] [handleNonAuthRequest] ✗ Request FAILED`);
        console.log(`[Auth-Interceptor #${requestId}] [handleNonAuthRequest]   Error status: ${error.status}`);
        console.log(`[Auth-Interceptor #${requestId}] [handleNonAuthRequest]   Error message: ${error.message}`);
        console.log(`[Auth-Interceptor #${requestId}] [handleNonAuthRequest]   Error details:`, error.error);
        console.log(`[Auth-Interceptor #${requestId}] [handleNonAuthRequest]   Error stack:`, error);

        // Some endpoints may still need auth despite being in skip list
        if (this.shouldRetryWithAuth(req, error, requestId)) {
          console.log(`[Auth-Interceptor #${requestId}] [handleNonAuthRequest] → Decision: RETRY with authentication`);
          return this.acquireTokenAndExecute(req, next, requestId);
        }

        console.log(`[Auth-Interceptor #${requestId}] [handleNonAuthRequest] → Decision: PROPAGATE error (no retry)`);
        return throwError(() => error);
      })
    );
  }

  /**
   * Checks if a non-auth request error should trigger auth retry
   */
  private shouldRetryWithAuth(req: HttpRequest<any>, error: HttpErrorResponse, requestId: number): boolean {
    const isAuthError = error.status === 401 || error.status === 400;
    const isApiEndpoint = 
      req.url.includes("oem/api/") || 
      req.url.includes("/auth/api/v2");
    const isNotExcluded = 
      !req.url.includes("damages/detail/docs") &&
      !req.url.includes("attachment") &&
      !req.url.includes("assets/version.json");

    const shouldRetry = isAuthError && isApiEndpoint && isNotExcluded;

    console.log(`[Auth-Interceptor #${requestId}] [shouldRetryWithAuth] Evaluation:`, {
      shouldRetry,
      conditions: {
        isAuthError: `${isAuthError} (status: ${error.status})`,
        isApiEndpoint,
        isNotExcluded
      }
    });

    return shouldRetry;
  }

  /**
   * Handles requests that require authentication
   * Acquires token and retries on auth errors
   */
  private handleAuthRequest(
    req: HttpRequest<any>,
    next: HttpHandler,
    requestId: number
  ): Observable<HttpEvent<any>> {
    console.log(`[Auth-Interceptor #${requestId}] [handleAuthRequest] Starting authenticated request flow`);
    
    return this.acquireTokenAndExecute(req, next, requestId).pipe(
      catchError((error: any) => {
        console.log(`[Auth-Interceptor #${requestId}] [handleAuthRequest] ✗ Authenticated request FAILED`);
        console.log(`[Auth-Interceptor #${requestId}] [handleAuthRequest]   Error status: ${error.status || 'N/A'}`);
        console.log(`[Auth-Interceptor #${requestId}] [handleAuthRequest]   Error message: ${error.message || 'N/A'}`);
        console.log(`[Auth-Interceptor #${requestId}] [handleAuthRequest]   Error code: ${error.errorCode || 'N/A'}`);
        console.log(`[Auth-Interceptor #${requestId}] [handleAuthRequest]   Error details:`, error.error || error);
        console.log(`[Auth-Interceptor #${requestId}] [handleAuthRequest]   Full error object:`, error);

        // Check for MSAL timeout
        if (error instanceof TimeoutError) {
          console.log(`[Auth-Interceptor #${requestId}] [handleAuthRequest] ⚠ CRITICAL: MSAL operation timed out`);
          console.log(`[Auth-Interceptor #${requestId}] [handleAuthRequest]   → Decision: ABORT retry (timeout)`);
          this.clearRetryCount(req);
          return throwError(() => error);
        }

        // Never retry if MSAL interaction already in progress (prevents loops)
        if (this.isMsalInteractionInProgress(error, requestId)) {
          console.log(`[Auth-Interceptor #${requestId}] [handleAuthRequest] ⚠ CRITICAL: MSAL interaction already in progress`);
          console.log(`[Auth-Interceptor #${requestId}] [handleAuthRequest]   → Decision: ABORT retry to prevent infinite loop`);
          this.clearRetryCount(req);
          return throwError(() => error);
        }

        // Check retry limit
        const retryKey = this.getRequestId(req);
        const currentRetries = this.retryCount.get(retryKey) || 0;
        
        if (currentRetries >= this.MAX_RETRY_ATTEMPTS) {
          console.log(`[Auth-Interceptor #${requestId}] [handleAuthRequest] ⚠ CRITICAL: Max retry attempts reached (${currentRetries}/${this.MAX_RETRY_ATTEMPTS})`);
          console.log(`[Auth-Interceptor #${requestId}] [handleAuthRequest]   → Decision: ABORT retry to prevent infinite loop`);
          this.clearRetryCount(req);
          return throwError(() => error);
        }

        // Determine if we should attempt token refresh
        if (this.shouldAttemptTokenRefresh(req, error, requestId)) {
          this.retryCount.set(retryKey, currentRetries + 1);
          console.log(`[Auth-Interceptor #${requestId}] [handleAuthRequest] → Decision: ATTEMPT token refresh (retry ${currentRetries + 1}/${this.MAX_RETRY_ATTEMPTS})`);
          return this.acquireTokenAndExecute(req, next, requestId).pipe(
            finalize(() => {
              // Clear retry count on success or final failure
              this.clearRetryCount(req);
            })
          );
        }

        console.log(`[Auth-Interceptor #${requestId}] [handleAuthRequest] → Decision: PROPAGATE error (no refresh attempt)`);
        this.clearRetryCount(req);
        return throwError(() => error);
      })
    );
  }
  
  /**
   * Clear retry count for a request
   */
  private clearRetryCount(req: HttpRequest<any>): void {
    const requestId = this.getRequestId(req);
    this.retryCount.delete(requestId);
  }

  // ==================== ERROR ANALYSIS ====================

  /**
   * Detects if error is due to concurrent MSAL interaction
   */
  private isMsalInteractionInProgress(error: any, requestId: number): boolean {
    const hasErrorCode = error?.errorCode === 'interaction_in_progress';
    const hasMessageText = error?.message?.includes('interaction_in_progress');
    const result = hasErrorCode || hasMessageText;

    console.log(`[Auth-Interceptor #${requestId}] [isMsalInteractionInProgress] Check:`, {
      result,
      errorCode: error?.errorCode,
      hasErrorCode,
      hasMessageText,
      errorMessage: error?.message
    });

    return result;
  }

  /**
   * Determines if error warrants token refresh attempt
   */
  private shouldAttemptTokenRefresh(req: HttpRequest<any>, error: any, requestId: number): boolean {
    console.log(`[Auth-Interceptor #${requestId}] [shouldAttemptTokenRefresh] Evaluating...`);
    console.log(`[Auth-Interceptor #${requestId}] [shouldAttemptTokenRefresh]   Request URL: ${req.url}`);
    console.log(`[Auth-Interceptor #${requestId}] [shouldAttemptTokenRefresh]   Request method: ${req.method}`);
    console.log(`[Auth-Interceptor #${requestId}] [shouldAttemptTokenRefresh]   Error status: ${error.status || 'undefined'}`);

    // Never retry for special URLs (downloads, attachments)
    if (this.isNonRefreshableUrl(req, requestId)) {
      console.log(`[Auth-Interceptor #${requestId}] [shouldAttemptTokenRefresh]   ✗ REJECTED: Non-refreshable URL (attachment/docs)`);
      return false;
    }

    // Never retry non-GET requests (could duplicate operations)
    if (req.method.toUpperCase() !== "GET") {
      console.log(`[Auth-Interceptor #${requestId}] [shouldAttemptTokenRefresh]   ✗ REJECTED: Non-GET request (${req.method})`);
      console.log(`[Auth-Interceptor #${requestId}] [shouldAttemptTokenRefresh]      Reason: Could duplicate POST/PUT/DELETE operations`);
      return false;
    }

    // Never retry server errors or not found
    if (error.status >= 500) {
      console.log(`[Auth-Interceptor #${requestId}] [shouldAttemptTokenRefresh]   ✗ REJECTED: Server error (${error.status})`);
      console.log(`[Auth-Interceptor #${requestId}] [shouldAttemptTokenRefresh]      Reason: Backend issue, not auth issue`);
      return false;
    }

    if (error.status === 404) {
      console.log(`[Auth-Interceptor #${requestId}] [shouldAttemptTokenRefresh]   ✗ REJECTED: Not found (404)`);
      console.log(`[Auth-Interceptor #${requestId}] [shouldAttemptTokenRefresh]      Reason: Resource doesn't exist`);
      return false;
    }

    // Retry for likely auth issues (401, 403, token expired, etc.)
    console.log(`[Auth-Interceptor #${requestId}] [shouldAttemptTokenRefresh]   ✓ ACCEPTED: Eligible for token refresh`);
    console.log(`[Auth-Interceptor #${requestId}] [shouldAttemptTokenRefresh]      Reason: Likely auth issue (status: ${error.status})`);
    return true;
  }

  // ==================== TOKEN ACQUISITION ====================

  /**
   * Main token acquisition method with progressive fallback
   * Strategy: silent → popup → redirect (last resort)
   * 
   * Uses shared observable to prevent multiple concurrent token requests
   * when many HTTP requests fail simultaneously
   */
  private acquireTokenAndExecute(
    req: HttpRequest<any>,
    next: HttpHandler,
    requestId: number
  ): Observable<HttpEvent<any>> {
    console.log(`[Auth-Interceptor #${requestId}] [acquireTokenAndExecute] Entry point`);
    
    // If token refresh already in progress, reuse it (prevents multiple popups)
    if (this.tokenRefreshInProgress$) {
      console.log(`[Auth-Interceptor #${requestId}] ⏳ Token refresh ALREADY IN PROGRESS`);
      console.log(`[Auth-Interceptor #${requestId}]   → Strategy: JOIN existing request queue`);
      console.log(`[Auth-Interceptor #${requestId}]   → Benefit: Prevents multiple concurrent popups`);
      return this.tokenRefreshInProgress$.pipe(
        tap(() => console.log(`[Auth-Interceptor #${requestId}]   ✓ Received token from shared observable`)),
        switchMap(token => this.executeRequestWithToken(req, next, token, requestId))
      );
    }

    // Atomic check-and-set to prevent race conditions
    if (this.isAcquiringToken) {
      console.log(`[Auth-Interceptor #${requestId}] ⚠ Race condition detected: another thread is creating observable`);
      console.log(`[Auth-Interceptor #${requestId}]   → Waiting 50ms and retrying...`);
      return new Observable(observer => {
        setTimeout(() => {
          this.acquireTokenAndExecute(req, next, requestId).subscribe(observer);
        }, 50);
      });
    }

    console.log(`[Auth-Interceptor #${requestId}] 🔑 INITIATING NEW token acquisition sequence`);
    console.log(`[Auth-Interceptor #${requestId}]   Creating shared observable for concurrent requests`);
    
    this.isAcquiringToken = true;

    // Create shared observable for token acquisition
    this.tokenRefreshInProgress$ = this.acquireTokenWithFallback(requestId).pipe(
      finalize(() => {
        // Clear flags when done (success or error) - executed ONCE at source completion
        console.log(`[Auth-Interceptor #${requestId}] ✓ Token acquisition sequence FINALIZED`);
        console.log(`[Auth-Interceptor #${requestId}]   Clearing tokenRefreshInProgress$ and isAcquiringToken flags`);
        this.tokenRefreshInProgress$ = null;
        this.isAcquiringToken = false;
      }),
      shareReplay({ bufferSize: 1, refCount: true }) // Share result with all subscribers
    );

    return this.tokenRefreshInProgress$.pipe(
      switchMap(token => this.executeRequestWithToken(req, next, token, requestId))
    );
  }

  /**
   * Helper to extract expiration from idToken JWT
   */
  private getIdTokenExpiration(idToken: string | undefined): { expiresAt: Date | null, minutesRemaining: number } {
    if (!idToken) {
      return { expiresAt: null, minutesRemaining: -1 };
    }

    try {
      const payloadBase64 = idToken.split('.')[1];
      if (!payloadBase64) {
        return { expiresAt: null, minutesRemaining: -1 };
      }

      const payloadJson = atob(payloadBase64.replace(/-/g, '+').replace(/_/g, '/'));
      const payload = JSON.parse(payloadJson);

      if (payload.exp) {
        const expiresAt = new Date(payload.exp * 1000);
        const now = new Date();
        const minutesRemaining = Math.floor((expiresAt.getTime() - now.getTime()) / 60000);
        return { expiresAt, minutesRemaining };
      }
    } catch (error) {
      console.error('Error decoding idToken:', error);
    }

    return { expiresAt: null, minutesRemaining: -1 };
  }

  /**
   * Check if idToken is expired or about to expire
   */
  private isIdTokenValid(idToken: string | undefined, requestId: number, minMinutesRemaining: number = 10): boolean {
    const { expiresAt, minutesRemaining } = this.getIdTokenExpiration(idToken);
    
    if (!expiresAt) {
      console.log(`[Auth-Interceptor #${requestId}] [isIdTokenValid] ✗ Cannot decode idToken expiration`);
      return false;
    }

    const isValid = minutesRemaining >= minMinutesRemaining;
    
    console.log(`[Auth-Interceptor #${requestId}] [isIdTokenValid] idToken Check:`, {
      expiresAt: expiresAt.toISOString(),
      minutesRemaining,
      minRequired: minMinutesRemaining,
      isValid
    });

    return isValid;
  }

  /**
   * Progressive token acquisition with fallback chain
   * 1. Try silent (no UI) - best case
   * 2. Try popup (modal) - good UX
   * 3. Use redirect (full page) - last resort only if popup blocked
   */
  private acquireTokenWithFallback(requestId: number): Observable<any> {
    const scopes = environment.clientScope.split(" ");

    console.log(`[Auth-Interceptor #${requestId}] [acquireTokenWithFallback] FALLBACK CHAIN START`);
    console.log(`[Auth-Interceptor #${requestId}]   Scopes requested:`, scopes);
    console.log(`[Auth-Interceptor #${requestId}]   Strategy: Silent → Popup → Redirect`);
    
    // Get account explicitly to avoid no_account_error timing issues
    const account = this.msalService.instance.getActiveAccount() || 
                    this.msalService.instance.getAllAccounts()[0];
    
    if (!account) {
      console.log(`[Auth-Interceptor #${requestId}] [acquireTokenWithFallback]   ✗ CRITICAL: No accounts available in MSAL`);
      console.log(`[Auth-Interceptor #${requestId}] [acquireTokenWithFallback]   ✗ This should not happen - MsalGuard should prevent this`);
      console.log(`[Auth-Interceptor #${requestId}] [acquireTokenWithFallback]   ✗ Possible causes: logout, cache cleared, or race condition during login`);
      console.log(`[Auth-Interceptor #${requestId}] [acquireTokenWithFallback]   → Skipping silent auth, going directly to interactive`);
    } else {
      if (!this.msalService.instance.getActiveAccount()) {
        console.log(`[Auth-Interceptor #${requestId}] [acquireTokenWithFallback]   ⚠ WARNING: activeAccount not set yet, using first available account`);
      }
      console.log(`[Auth-Interceptor #${requestId}] [acquireTokenWithFallback]   Account being used:`, account);
    }
    
    console.log(`[Auth-Interceptor #${requestId}] → STEP 1/3: acquireTokenSilent (no UI)`);
    
    return this.msalService.acquireTokenSilent({ scopes, account }).pipe(
      timeout(this.MSAL_TIMEOUT),
      tap(token => {
        console.log(`[Auth-Interceptor #${requestId}] [acquireTokenWithFallback] ✓ STEP 1/3 SUCCESS: Silent token acquired`);
        console.log(`[Auth-Interceptor #${requestId}] [acquireTokenWithFallback]   Token type: ${token.tokenType}`);
        
        // Log idToken expiration (what we actually use)
        const { expiresAt, minutesRemaining } = this.getIdTokenExpiration(token.idToken);
        if (expiresAt) {
          console.log(`[Auth-Interceptor #${requestId}] [acquireTokenWithFallback]   ⚠ idToken expires: ${expiresAt.toISOString()} (${minutesRemaining} min remaining)`);
        }
        
        // Log accessToken expiration (for comparison)
        console.log(`[Auth-Interceptor #${requestId}] [acquireTokenWithFallback]   accessToken expires: ${new Date(token.expiresOn || '').toISOString()} (reference only, not used)`);
        console.log(`[Auth-Interceptor #${requestId}] [acquireTokenWithFallback]   Account: ${token.account?.username || 'N/A'}`);
        console.log(`[Auth-Interceptor #${requestId}] [acquireTokenWithFallback]   FULL TOKEN OBJECT:`, token);
      }),
      switchMap(token => {
        // Validate idToken before using it
        if (!this.isIdTokenValid(token.idToken, requestId, 10)) {
          console.log(`[Auth-Interceptor #${requestId}] [acquireTokenWithFallback] ⚠ WARNING: idToken expired or expiring soon`);
          console.log(`[Auth-Interceptor #${requestId}] [acquireTokenWithFallback]   → Force refreshing token with forceRefresh=true`);
          
          // Force refresh to get a new idToken
          return this.msalService.acquireTokenSilent({ 
            scopes, 
            account,
            forceRefresh: true 
          }).pipe(
            timeout(this.MSAL_TIMEOUT),
            tap(refreshedToken => {
              const { expiresAt, minutesRemaining } = this.getIdTokenExpiration(refreshedToken.idToken);
              console.log(`[Auth-Interceptor #${requestId}] [acquireTokenWithFallback] ✓ Token REFRESHED successfully`);
              console.log(`[Auth-Interceptor #${requestId}] [acquireTokenWithFallback]   New idToken expires: ${expiresAt?.toISOString()} (${minutesRemaining} min)`);
            })
          );
        }
        
        // Token is valid, use it
        return of(token);
      }),
      catchError(silentError => {
        console.log(`[Auth-Interceptor #${requestId}] [acquireTokenWithFallback] ✗ STEP 1/3 FAILED: Silent acquisition error`);
        console.log(`[Auth-Interceptor #${requestId}] [acquireTokenWithFallback]   Error code: ${silentError.errorCode}`);
        console.log(`[Auth-Interceptor #${requestId}] [acquireTokenWithFallback]   Error message: ${silentError.message}`);
        console.log(`[Auth-Interceptor #${requestId}] [acquireTokenWithFallback]   Error details:`, silentError);

        // Check if interaction already in progress
        if (this.isMsalInteractionInProgress(silentError, requestId)) {
          console.log(`[Auth-Interceptor #${requestId}] [acquireTokenWithFallback] ⚠ CRITICAL: Another MSAL interaction in progress`);
          console.log(`[Auth-Interceptor #${requestId}] [acquireTokenWithFallback]   → ABORTING entire chain to prevent conflicts`);
          return throwError(() => silentError);
        }

        console.log(`[Auth-Interceptor #${requestId}] [acquireTokenWithFallback] → STEP 2/3: acquireTokenPopup (modal UI)`);
        console.log(`[Auth-Interceptor #${requestId}] [acquireTokenWithFallback]   Reason: Silent failed, user interaction required`);
        
        return this.msalService.acquireTokenPopup({ scopes }).pipe(
          timeout(this.MSAL_TIMEOUT),
          tap(token => {
            console.log(`[Auth-Interceptor #${requestId}] [acquireTokenWithFallback] ✓ STEP 2/3 SUCCESS: Popup token acquired`);
            console.log(`[Auth-Interceptor #${requestId}] [acquireTokenWithFallback]   Token type: ${token.tokenType}`);
            
            const { expiresAt, minutesRemaining } = this.getIdTokenExpiration(token.idToken);
            if (expiresAt) {
              console.log(`[Auth-Interceptor #${requestId}] [acquireTokenWithFallback]   ⚠ idToken expires: ${expiresAt.toISOString()} (${minutesRemaining} min remaining)`);
            }
            
            console.log(`[Auth-Interceptor #${requestId}] [acquireTokenWithFallback]   accessToken expires: ${new Date(token.expiresOn || '').toISOString()} (reference only)`);
            console.log(`[Auth-Interceptor #${requestId}] [acquireTokenWithFallback]   Account: ${token.account?.username || 'N/A'}`);
            console.log(`[Auth-Interceptor #${requestId}] [acquireTokenWithFallback]   FULL TOKEN OBJECT:`, token);
          }),
          catchError(popupError => {
            console.log(`[Auth-Interceptor #${requestId}] [acquireTokenWithFallback] ✗ STEP 2/3 FAILED: Popup acquisition error`);
            console.log(`[Auth-Interceptor #${requestId}] [acquireTokenWithFallback]   Error code: ${popupError.errorCode}`);
            console.log(`[Auth-Interceptor #${requestId}] [acquireTokenWithFallback]   Error message: ${popupError.message}`);
            console.log(`[Auth-Interceptor #${requestId}] [acquireTokenWithFallback]   Error details:`, popupError);

            // Only use redirect if popup was blocked by browser
            if (this.isPopupBlockedError(popupError, requestId)) {
              console.log(`[Auth-Interceptor #${requestId}] [acquireTokenWithFallback] ⚠ Popup BLOCKED by browser settings`);
              console.log(`[Auth-Interceptor #${requestId}] [acquireTokenWithFallback] → STEP 3/3: acquireTokenRedirect (full page redirect)`);
              console.log(`[Auth-Interceptor #${requestId}] [acquireTokenWithFallback]   ⚠ WARNING: Page will reload after authentication`);
              console.log(`[Auth-Interceptor #${requestId}] [acquireTokenWithFallback]   ⚠ WARNING: This is the LAST RESORT fallback`);
              
              // Redirect doesn't return - page will reload
              this.msalService.acquireTokenRedirect({ scopes });
              console.log(`[Auth-Interceptor #${requestId}] [acquireTokenWithFallback]   Redirect initiated... observable completing`);
              return EMPTY; // Complete the observable
            }

            console.log(`[Auth-Interceptor #${requestId}] [acquireTokenWithFallback]   → Popup error is NOT browser-block, propagating`);
            return throwError(() => popupError);
          })
        );
      })
    );
  }

  /**
   * Detects if popup was blocked by browser
   */
  private isPopupBlockedError(error: any, requestId: number): boolean {
    const hasPopupWindowError = error?.errorCode === 'popup_window_error';
    const hasPopupWindowMessage = error?.message?.includes('popup_window_error');
    const hasEmptyWindowMessage = error?.message?.includes('empty_window_error');
    const result = hasPopupWindowError || hasPopupWindowMessage || hasEmptyWindowMessage;

    console.log(`[Auth-Interceptor #${requestId}] [isPopupBlockedError] Check:`, {
      result,
      errorCode: error?.errorCode,
      hasPopupWindowError,
      hasPopupWindowMessage,
      hasEmptyWindowMessage,
      errorMessage: error?.message
    });

    if (result) {
      console.log(`[Auth-Interceptor #${requestId}]   ⚠ CONFIRMED: Popup blocked by browser`);
    }

    return result;
  }

  /**
   * Executes HTTP request with provided token
   */
  private executeRequestWithToken(
    req: HttpRequest<any>,
    next: HttpHandler,
    token: any,
    requestId: number
  ): Observable<HttpEvent<any>> {
    console.log(`[Auth-Interceptor #${requestId}] [executeRequestWithToken] Preparing authorized request`);
    console.log(`[Auth-Interceptor #${requestId}] [executeRequestWithToken]   Original URL: ${req.url}`);
    console.log(`[Auth-Interceptor #${requestId}] [executeRequestWithToken]   Token available: ${!!token}`);
    console.log(`[Auth-Interceptor #${requestId}] [executeRequestWithToken]   FULL TOKEN OBJECT:`, token);
    
    const headers = new HttpHeaders()
      .set("Authorization", `Bearer ${token.idToken}`)
      .set("Access-Control-Allow-Origin", "*");

    console.log(`[Auth-Interceptor #${requestId}] [executeRequestWithToken]   Authorization header set with token`);

    const authorizedReq = req.clone({ headers });
    console.log(`[Auth-Interceptor #${requestId}] [executeRequestWithToken]   → Sending authorized request...`);

    return next.handle(authorizedReq).pipe(
      tap((event: any) => {
        if (event.type === 4) { // HttpResponse
          console.log(`[Auth-Interceptor #${requestId}] [executeRequestWithToken] ✓ Request COMPLETED successfully`);
          console.log(`[Auth-Interceptor #${requestId}] [executeRequestWithToken]   Response status: ${event.status}`);
          console.log(`[Auth-Interceptor #${requestId}] [executeRequestWithToken]   Response body:`, event.body);
        }
      }),
      catchError((error: HttpErrorResponse) => {
        console.log(`[Auth-Interceptor #${requestId}] [executeRequestWithToken] ✗ Request FAILED even with valid token`);
        console.log(`[Auth-Interceptor #${requestId}] [executeRequestWithToken]   Error status: ${error.status}`);
        console.log(`[Auth-Interceptor #${requestId}] [executeRequestWithToken]   Error message: ${error.message}`);
        console.log(`[Auth-Interceptor #${requestId}] [executeRequestWithToken]   Error URL: ${error.url}`);
        console.log(`[Auth-Interceptor #${requestId}] [executeRequestWithToken]   Error details:`, error.error);
        console.log(`[Auth-Interceptor #${requestId}] [executeRequestWithToken]   Full error:`, error);
        
        if (error.status === 401) {
          console.log(`[Auth-Interceptor #${requestId}] [executeRequestWithToken]   ⚠ WARNING: 401 with fresh token = deeper auth issue`);
          console.log(`[Auth-Interceptor #${requestId}] [executeRequestWithToken]   Possible causes: invalid permissions, token validation failed on backend`);
        }
        
        console.log(`[Auth-Interceptor #${requestId}] [executeRequestWithToken]   → Decision: Propagate error (no infinite retry)`);
        return throwError(() => error);
      })
    );
  }
}
